import { LitElement, css, html } from 'lit';
import { customElement } from 'lit/decorators.js';

/**
 * Generic animated skeleton shown in the router outlet while a lazily loaded
 * route is being fetched (2026-09-16 perceived-latency work).
 *
 * On a slow connection a route change can take several seconds because the
 * router must download the page chunk before it can navigate — during that
 * wait the old view simply sits there and looks frozen. `tfp-app` appends
 * this element to `#mainview` the moment a navigation starts (the
 * `vaadin-router-go` event fires in the same task as the tap) and removes
 * it when the new location is committed, so the UI reacts instantly.
 *
 * It is deliberately generic: a header bar, a hero block, text lines and a
 * card row in the app's own surface tokens. It carries no data and has no
 * API — it just shimmers until the real page replaces it. Honors
 * `prefers-reduced-motion`.
 */
@customElement('tfp-route-skeleton')
export class TFPRouteSkeleton extends LitElement {
  static styles = css`
    :host {
      position: absolute;
      inset: 0;
      z-index: 5;
      display: block;
      /* Swallow taps while the new page loads: a second navigation during
         the wait would only queue behind the first one anyway. */
      pointer-events: auto;
      background-color: var(--tfp-md-color-background, #121212);
      overflow: hidden;
      animation: skeleton-fade-in 120ms ease-out;
    }

    .wrap {
      display: grid;
      grid-template-columns: 1fr;
      align-content: start;
      gap: 14px;
      padding: 16px;
      height: 100%;
      box-sizing: border-box;
    }

    .bar {
      border-radius: var(--tfp-md-shape-corner-small, 8px);
      background-color: var(--tfp-md-color-surface-variant, #2b2b2b);
      background-image: linear-gradient(
        100deg,
        transparent 20%,
        color-mix(in srgb, var(--tfp-md-color-on-surface, #e6e6e6) 8%, transparent) 50%,
        transparent 80%
      );
      background-size: 250% 100%;
      background-position: 130% 0;
      animation: skeleton-shimmer 1.4s ease-in-out infinite;
    }

    .header {
      width: 45%;
      height: 22px;
    }

    .hero {
      width: 100%;
      aspect-ratio: 16 / 9;
      max-height: 40vh;
      border-radius: var(--tfp-md-shape-corner-medium, 16px);
    }

    .line {
      height: 14px;
    }

    .cards {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 12px;
    }

    .card {
      aspect-ratio: 1 / 1;
      border-radius: var(--tfp-md-shape-corner-medium, 16px);
    }

    @keyframes skeleton-shimmer {
      from { background-position: 130% 0; }
      to { background-position: -30% 0; }
    }

    @keyframes skeleton-fade-in {
      from { opacity: 0; }
    }

    @media (prefers-reduced-motion: reduce) {
      .bar {
        animation: none;
        background-image: none;
      }
    }
  `;

  protected render() {
    return html`
      <div class="wrap" part="skeleton">
        <div class="bar header"></div>
        <div class="bar hero"></div>
        <div class="bar line" style="width: 85%"></div>
        <div class="bar line" style="width: 60%"></div>
        <div class="cards">
          <div class="bar card"></div>
          <div class="bar card"></div>
          <div class="bar card"></div>
        </div>
        <div class="bar line" style="width: 92%"></div>
        <div class="bar line" style="width: 40%"></div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'tfp-route-skeleton': TFPRouteSkeleton;
  }
}
